import type { AttributeTracker } from "./attributes";
import { extractContactSummaries, type ContactSummary } from "./contactId";
import { createLogger } from "./logger";
import type { RequestRunner } from "./requestRunner";
import type { SubscriptionManager } from "./subscriptions";

const log = createLogger("probe");

/** Live agent facts pulled on connect. Fields stay unset when a probe call fails or is mock. */
export type AgentSnapshot = {
  name?: string;
  arn?: string;
  availability?: unknown;
  state?: unknown;
  routingProfile?: unknown;
  concurrency?: unknown;
  extension?: unknown;
  network?: unknown;
  permissions?: unknown;
};

/** Result of {@link probeSession}: subscriptions armed plus the current workspace snapshot. */
export type ProbeResult = {
  subscribed: number;
  agent: AgentSnapshot;
  contacts: ContactSummary[];
};

/** Dependencies {@link probeSession} needs from the connected App. */
export type ProbeDeps = {
  manager: SubscriptionManager;
  runner: RequestRunner;
  tracker: AttributeTracker;
};

const AGENT_PROBES: ReadonlyArray<{ method: string; field: keyof AgentSnapshot }> = [
  { method: "getName", field: "name" },
  { method: "getARN", field: "arn" },
  { method: "getAvailabilityState", field: "availability" },
  { method: "getState", field: "state" },
  { method: "getRoutingProfile", field: "routingProfile" },
  { method: "getChannelConcurrency", field: "concurrency" },
  { method: "getExtension", field: "extension" },
  { method: "getNetworkConnectionStatus", field: "network" },
  { method: "listSecurityProfilePermissions", field: "permissions" },
];

const USER_PROBES = ["getUserArn", "getUserRegion", "getInstanceId"] as const;

/**
 * Drops MockGateway's `{ __mock: true }` stand-in so the roster never displays synthetic
 * objects as if they were live agent/contact data.
 */
function livePayload(payload: unknown): unknown {
  if (payload && typeof payload === "object" && Object.hasOwn(payload, "__mock")) {
    return undefined;
  }
  return payload;
}

/** Pulls zero-arg agent identity/state methods used to populate the roster. */
async function probeAgent(runner: RequestRunner): Promise<AgentSnapshot> {
  const agent: AgentSnapshot = {};
  for (const { method, field } of AGENT_PROBES) {
    const entry = await runner.invoke("agent", method, {});
    if (entry.kind === "error") continue;
    const value = livePayload(entry.payload);
    if (value !== undefined) (agent as Record<string, unknown>)[field] = value;
  }
  return agent;
}

/** Records user/instance identity. Failures become timeline error entries, not roster fields. */
async function probeUser(runner: RequestRunner): Promise<void> {
  for (const method of USER_PROBES) {
    await runner.invoke("user", method, {});
  }
}

/**
 * Lists contacts already in the workspace (idle, on a chat, mid-transfer, being monitored)
 * and snapshots each one so the roster is populated without waiting for a new event.
 */
async function probeContacts(
  runner: RequestRunner,
  tracker: AttributeTracker,
): Promise<ContactSummary[]> {
  const entry = await runner.invoke("contact", "listContacts", {});
  const contacts = extractContactSummaries(
    entry.kind === "error" ? undefined : entry.payload,
  );
  for (const c of contacts) {
    await tracker.capture(c.contactId);
    await runner.invoke("contact", "listParticipants", { contactId: c.contactId });
  }
  return contacts;
}

/**
 * Arms every auto-subscribable event, then pulls agent details and current contacts.
 * Call once on handshake (`connected` or `mock`). Interceptors stay off.
 */
export async function probeSession(deps: ProbeDeps): Promise<ProbeResult> {
  log.info("probe start");
  const subscribed = deps.manager.subscribeAll();
  log.info("subscriptions armed", {
    subscribed,
    keys: deps.manager.activeKeys(),
  });
  const agent = await probeAgent(deps.runner);
  log.info("agent probe done", {
    name: agent.name,
    availability: agent.availability,
    concurrency: agent.concurrency,
  });
  await probeUser(deps.runner);
  const contacts = await probeContacts(deps.runner, deps.tracker);
  log.info("probe complete", {
    subscribed,
    contactCount: contacts.length,
    contactIds: contacts.map((c) => c.contactId),
  });
  return { subscribed, agent, contacts };
}
